import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";

import { LoadingSpinner } from "../../components/LoadingSpinner";
import { useGetChatQuery } from "../../store/queries/chat";
import { IChatMessage } from "../../types/Chat";

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const HeaderCard = styled(Card)`
  margin-bottom: 24px;
`;

const MessagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 600px;
  overflow-y: auto;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const MessageBubble = styled.div<{ isUser: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.isUser ? "flex-end" : "flex-start")};
  margin-bottom: 12px;
`;

const MessageContent = styled.div<{ isUser: boolean }>`
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 18px;
  background-color: ${(props) => (props.isUser ? "#007bff" : "#e9ecef")};
  color: ${(props) => (props.isUser ? "white" : "#333")};
  word-wrap: break-word;
  white-space: pre-wrap;
`;

const MessageMeta = styled.div<{ isUser: boolean }>`
  font-size: 0.75rem;
  color: #6c757d;
  margin-top: 4px;
  text-align: ${(props) => (props.isUser ? "right" : "left")};
`;

const SenderName = styled.span`
  font-weight: 600;
  margin-right: 8px;
`;

const BackButton = styled(Button)`
  margin-bottom: 16px;
`;

const ChatInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoLabel = styled.span`
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 600;
`;

const InfoValue = styled.span`
  font-size: 1rem;
  color: #333;
`;

const LinkText = styled.span`
  color: #0066cc;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

function ChatViewPage() {
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();
  const {
    data: chat,
    isLoading,
    error,
  } = useGetChatQuery(chatId || "", {
    skip: !chatId,
  });

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getSenderName = (role: "user" | "assistant") => {
    return role === "user" ? "Patient" : "Clara";
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !chat) {
    return (
      <Container>
        <Card>
          <h2>Error</h2>
          <p>Chat not found or failed to load.</p>
          <Button
            label="Back to Chats"
            icon="pi pi-arrow-left"
            onClick={() => navigate("/chatbot/chats")}
          />
        </Card>
      </Container>
    );
  }

  const messages = (chat.chatMessages || []) as IChatMessage[];

  return (
    <Container>
      <BackButton
        label="Back to Chats"
        icon="pi pi-arrow-left"
        className="p-button-text p-button-secondary"
        onClick={() => navigate("/chatbot/chats")}
      />

      <HeaderCard>
        <h1>Chat Conversation</h1>
        <ChatInfo>
          <InfoItem>
            <InfoLabel>Chat ID:</InfoLabel>
            <InfoValue>{chat.id}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Account:</InfoLabel>
            <InfoValue>
              <LinkText
                onClick={() => navigate(`/hospitals/view/${chat.accountId}`)}
              >
                {chat.account.name}
              </LinkText>
            </InfoValue>
          </InfoItem>
          {chat.data?.orderId && (
            <InfoItem>
              <InfoLabel>Order:</InfoLabel>
              <InfoValue>
                <LinkText
                  onClick={() =>
                    chat.data?.orderId &&
                    navigate(`/orders/${chat.data.orderId}`)
                  }
                >
                  {chat.data.orderId}
                </LinkText>
              </InfoValue>
            </InfoItem>
          )}
          <InfoItem>
            <InfoLabel>Model:</InfoLabel>
            <InfoValue>
              <LinkText
                onClick={() => navigate(`/chatbot/models/${chat.chatModelId}`)}
              >
                {chat.chatModel.name}
              </LinkText>
            </InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Started:</InfoLabel>
            <InfoValue>{formatTimestamp(chat.createdAt)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Last Updated:</InfoLabel>
            <InfoValue>{formatTimestamp(chat.updatedAt)}</InfoValue>
          </InfoItem>
        </ChatInfo>
      </HeaderCard>

      <Card title={`Messages (${messages.length})`}>
        {messages.length > 0 ? (
          <MessagesContainer>
            {messages.map((message, index) => {
              const isUser = message.role === "user";
              return (
                <MessageBubble key={index} isUser={isUser}>
                  <MessageContent isUser={isUser}>
                    {message.content}
                  </MessageContent>
                  <MessageMeta isUser={isUser}>
                    <SenderName>{getSenderName(message.role)}</SenderName>
                    {formatTimestamp(message.timestamp)}
                  </MessageMeta>
                </MessageBubble>
              );
            })}
          </MessagesContainer>
        ) : (
          <p className="text-muted">No messages in this conversation yet.</p>
        )}
      </Card>
    </Container>
  );
}

export default ChatViewPage;
